/**
 * Untappd user filter — badge-only
 *
 * Visitor types their Untappd username in the footer; the JS scrapes the
 * public profile via codetabs CORS-proxy and tags matching product cards
 * with an "Al geproefd" badge + a grayscale/fade effect.
 *
 * No filter modes (no hide / only / off) — always shows badges, never
 * hides anything. Pagination and collection counts are left untouched.
 *
 * Matching is by Untappd beer ID. Product cards carry a
 * data-untappd-beer-id attribute (see snippets/product-card.liquid). The
 * ID is taken from the `untappd.beer_id` metafield, with a fallback to
 * the trailing numeric segment of `untappd.url`.
 *
 * Coverage caveat: Untappd caps non-authenticated views at 25 beers per
 * filter (?brewery_id=X, ?style_id=X, ?country_id=X). For an account
 * with 18 breweries that have >25 check-ins we can't reach those tails,
 * so realistic coverage is ~70% of total check-ins.
 */
(function () {
  if (window.__untappdUserFilter) return;

  // -------------------------------------------------------------------
  // Config
  // -------------------------------------------------------------------
  var STORAGE_KEY = 'hoppenaar:untappd-user-filter';
  // Target <product-card> elements specifically — not any element that
  // happens to carry an Untappd URL. The `.review-wrapper--untappd-badge`
  // overlay (rendered as a separate sibling on cards with as_badge style)
  // ALSO has data-untappd-url, which previously caused a second "Al geproefd"
  // badge to be stamped onto the rating overlay.
  var CARD_SELECTOR = 'product-card[data-untappd-beer-id], product-card[data-untappd-url*="untappd.com/b/"]';
  var BADGE_CLASS = 'untappd-tasted-badge';
  var WIDGET_SELECTOR = '[data-untappd-user-filter]';

  // Higher concurrency = faster scrape, but more proxy rate-limiting.
  // 20 is the sweet spot at codetabs's free tier — at 30 we lose ~40% of
  // fetches to 429s on big profiles (1500+ filter calls), which means
  // huge swathes of beer IDs go missing.
  var BREWERY_CONCURRENCY = 20;

  // Each filter call gets up to N retries on transient failure (429, 5xx,
  // network error, "Just a moment" CF challenge). The retry budget is
  // what makes coverage stable across scrapes — without it, an account
  // with 1500+ brewery calls loses ~40% of beers to random proxy hiccups.
  var FETCH_MAX_RETRIES = 3;

  // CORS-friendly proxy. Codetabs is the only proxy as of May 2026 that
  // reliably returns Access-Control-Allow-Origin AND gets through
  // Cloudflare's bot check on Untappd. If it ever goes down, the feature
  // breaks until we find another.
  var PROXIES = [
    function (url) { return 'https://api.codetabs.com/v1/proxy/?quest=' + url; }
  ];

  // Hoppenaar's own Cloudflare Worker. Used purely as a write-through cache
  // for scraped results — the actual Untappd scraping still happens in the
  // visitor's browser. A cache hit means another visitor of the same
  // Untappd username scraped within the last 48h, and we can skip straight
  // to applying badges (~200ms total).
  var WORKER_URL = 'https://hoppenaar-untappd.colin-43f.workers.dev';

  // -------------------------------------------------------------------
  // State (persistent across visits)
  // -------------------------------------------------------------------
  var state = loadState();
  var beerIdSet = new Set((state.beerIds || []).map(String));

  function loadState() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return defaultState();
      return {
        username: typeof parsed.username === 'string' ? parsed.username : '',
        beerIds: Array.isArray(parsed.beerIds) ? parsed.beerIds : [],
        breweryCount: typeof parsed.breweryCount === 'number' ? parsed.breweryCount : 0,
        // purchasedCount = how many of these beer IDs were tagged as bought
        // at De Hoppenaar in Untappd (comes from the Worker's KV cache,
        // populated by the daily /sync-purchased cron).
        purchasedCount: typeof parsed.purchasedCount === 'number' ? parsed.purchasedCount : 0,
        fetchedAt: typeof parsed.fetchedAt === 'number' ? parsed.fetchedAt : 0
      };
    } catch (e) {
      return defaultState();
    }
  }

  function defaultState() {
    return { username: '', beerIds: [], breweryCount: 0, purchasedCount: 0, fetchedAt: 0 };
  }

  function saveState() {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
    catch (e) { /* private mode etc. */ }
  }

  // -------------------------------------------------------------------
  // Proxy fetch
  // -------------------------------------------------------------------

  /**
   * Fetch a URL through the proxy chain. Returns HTML string on success,
   * null on total failure. Retries each proxy up to FETCH_MAX_RETRIES
   * times with exponential backoff before giving up.
   *
   * Failure modes that trigger retry:
   *   - 429 Too Many Requests (codetabs rate-limit)
   *   - 5xx upstream errors
   *   - response < 5kB (likely error page)
   *   - body contains "Just a moment" (CF challenge leaked through)
   *   - network error / fetch reject
   *
   * Without retry, an account with 1500+ filter calls loses ~40% of beers
   * to random proxy hiccups — this is the difference between SemVleeming
   * seeing 1,000 beers and seeing 7,500.
   */
  async function fetchViaProxy(url) {
    for (var i = 0; i < PROXIES.length; i++) {
      var proxiedUrl = PROXIES[i](url);
      for (var attempt = 0; attempt <= FETCH_MAX_RETRIES; attempt++) {
        var shouldRetry = false;
        try {
          var resp = await fetch(proxiedUrl, {
            headers: { 'Accept': 'text/html' },
            credentials: 'omit'
          });
          if (resp.status === 429 || (resp.status >= 500 && resp.status < 600)) {
            shouldRetry = true;
          } else if (!resp.ok) {
            // 4xx other than 429: probably a malformed URL, retry is pointless
            break;
          } else {
            var body = await resp.text();
            if (body && body.length >= 5000 && body.indexOf('Just a moment') === -1) {
              return body;
            }
            shouldRetry = true;
          }
        } catch (e) {
          shouldRetry = true;
        }
        if (!shouldRetry || attempt === FETCH_MAX_RETRIES) break;
        // Exponential backoff with jitter: 200ms, 500ms, 1100ms.
        var delay = 200 + attempt * 300 + Math.random() * 300;
        await new Promise(function (r) { setTimeout(r, delay); });
      }
    }
    return null;
  }

  // -------------------------------------------------------------------
  // Hoppenaar Flappy — a tiny canvas game shown during the background
  // scrape so visitors have something to do besides watch a progress %.
  //
  // - Beer can as the bird
  // - Hop-cone columns as obstacles
  // - Hoppenaar green palette
  // - localStorage high-score, persists across visits
  //
  // The game runs on requestAnimationFrame; stopping it cancels the RAF
  // and removes the canvas. Click / tap / Space to flap.
  // -------------------------------------------------------------------
  function HoppenaarFlappy(container) {
    var W = 320, H = 360;
    var DPR = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    var wrap = document.createElement('div');
    wrap.className = 'hoppenaar-flappy';
    wrap.setAttribute('aria-label', 'Hoppenaar Flappy spel');
    wrap.style.cssText =
      'margin:14px auto 4px;width:' + W + 'px;max-width:100%;text-align:center;' +
      'font-family:inherit;color:#000;';

    var label = document.createElement('div');
    label.style.cssText = 'font-size:11px;text-transform:uppercase;letter-spacing:0.08em;' +
                         'opacity:0.7;margin-bottom:6px;font-weight:700;';
    label.textContent = '🍺 Hoppenaar Flappy — klik om te springen';
    wrap.appendChild(label);

    var canvas = document.createElement('canvas');
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    canvas.style.cssText =
      'display:block;width:' + W + 'px;height:' + H + 'px;' +
      'border-radius:8px;cursor:pointer;background:linear-gradient(180deg,#a8cba0 0%,#385a3c 100%);' +
      'box-shadow:0 6px 16px rgba(0,0,0,0.18);touch-action:manipulation;outline:none;';
    canvas.setAttribute('tabindex', '0');
    wrap.appendChild(canvas);

    container.appendChild(wrap);

    var ctx = canvas.getContext('2d');
    ctx.scale(DPR, DPR);

    // ----- game state ----------------------------------------------
    // Tuned for forgiveness: gentle gravity, soft flap, slow scroll,
    // wide gaps. Designed so a casual visitor can keep the bird alive
    // long enough to enjoy it during a 30-second background scrape.
    var bird = { x: 80, y: H / 2, vy: 0, w: 30, h: 22 };
    var GRAVITY = 0.28;        // was 0.42 — softer fall
    var FLAP_VY = -5.6;        // was -6.6 — gentler hop
    var MAX_FALL_VY = 7.5;     // terminal velocity, prevents brutal dives
    var SCROLL = 1.7;          // was 2.2 — slower world
    var GAP_H = 135;           // was 110 — much bigger window
    var COL_W = 42;
    var COL_SPACING = 230;     // was 190 — more breathing room
    var HITBOX_SHRINK = 4;     // forgiveness on bird collisions (pixels per side)
    var obstacles = [];
    var score = 0;
    var highScore = 0;
    try { highScore = parseInt(localStorage.getItem('hoppenaar:flappy:hi') || '0', 10) || 0; }
    catch (_) {}
    var dead = false;
    var started = false;
    var running = true;
    var rafId = null;

    // Fixed-timestep accumulator. Game logic runs at exactly 60 updates/sec
    // regardless of monitor refresh rate (60Hz, 120Hz, 144Hz, ProMotion
    // 120Hz iPhone, etc.) so movement always feels consistent.
    var FIXED_DT_MS = 1000 / 60;
    var lastTime = 0;
    var accumulator = 0;

    function reset() {
      bird.y = H / 2; bird.vy = 0;
      obstacles = [];
      score = 0;
      dead = false;
      started = false;
    }

    function flap() {
      if (dead) { reset(); return; }
      started = true;
      bird.vy = FLAP_VY;
    }

    canvas.addEventListener('click', function (e) { e.preventDefault(); canvas.focus(); flap(); });
    canvas.addEventListener('touchstart', function (e) { e.preventDefault(); flap(); }, { passive: false });
    canvas.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault(); flap();
      }
    });

    function spawnIfNeeded() {
      var last = obstacles[obstacles.length - 1];
      if (!last || last.x < W - COL_SPACING) {
        var gapY = 50 + Math.random() * (H - 100 - GAP_H);
        obstacles.push({ x: W + 20, gapY: gapY, passed: false });
      }
    }

    function update() {
      if (!started) return;
      bird.vy += GRAVITY;
      if (bird.vy > MAX_FALL_VY) bird.vy = MAX_FALL_VY;
      bird.y += bird.vy;

      spawnIfNeeded();
      for (var i = obstacles.length - 1; i >= 0; i--) {
        var o = obstacles[i];
        o.x -= SCROLL;
        if (!o.passed && o.x + COL_W < bird.x - bird.w / 2) {
          o.passed = true;
          score++;
          if (score > highScore) {
            highScore = score;
            try { localStorage.setItem('hoppenaar:flappy:hi', String(highScore)); } catch (_) {}
          }
        }
        if (o.x < -COL_W - 10) obstacles.splice(i, 1);
      }

      // wall collisions
      if (bird.y + bird.h / 2 >= H - 4 || bird.y - bird.h / 2 <= 0) {
        bird.y = Math.max(bird.h / 2, Math.min(H - 4 - bird.h / 2, bird.y));
        dead = true;
      }
      // obstacle collisions — slightly shrunk hitbox for forgiveness
      var hbW = bird.w - HITBOX_SHRINK * 2;
      var hbH = bird.h - HITBOX_SHRINK * 2;
      for (var j = 0; j < obstacles.length; j++) {
        var ob = obstacles[j];
        var inX = bird.x + hbW / 2 > ob.x && bird.x - hbW / 2 < ob.x + COL_W;
        if (inX) {
          if (bird.y - hbH / 2 < ob.gapY || bird.y + hbH / 2 > ob.gapY + GAP_H) {
            dead = true;
          }
        }
      }
    }

    function drawObstacle(o) {
      // hop-cone columns: dark green body with a slightly lighter cap
      ctx.fillStyle = '#6c8a3a';
      ctx.fillRect(o.x, 0, COL_W, o.gapY);
      ctx.fillRect(o.x, o.gapY + GAP_H, COL_W, H - (o.gapY + GAP_H));
      ctx.fillStyle = '#86a448';
      ctx.fillRect(o.x - 3, o.gapY - 10, COL_W + 6, 10);
      ctx.fillRect(o.x - 3, o.gapY + GAP_H, COL_W + 6, 10);
      // hop pattern: simple darker dashes for texture
      ctx.fillStyle = 'rgba(0,0,0,0.12)';
      for (var y = 14; y < o.gapY - 14; y += 14) ctx.fillRect(o.x + 6, y, COL_W - 12, 3);
      for (var y2 = o.gapY + GAP_H + 18; y2 < H - 6; y2 += 14) ctx.fillRect(o.x + 6, y2, COL_W - 12, 3);
    }

    function drawBird() {
      // Tilt the can based on velocity: noses up when flapping, tips
      // forward when falling. Clamped so it never goes upside-down.
      var angle = Math.max(-0.45, Math.min(0.9, bird.vy * 0.07));

      ctx.save();
      ctx.translate(bird.x, bird.y);
      ctx.rotate(angle);

      var x = -bird.w / 2, y = -bird.h / 2;
      // can body — gold gradient
      var grad = ctx.createLinearGradient(x, y, x, y + bird.h);
      grad.addColorStop(0, '#e8c95a');
      grad.addColorStop(1, '#a78a26');
      ctx.fillStyle = grad;
      ctx.fillRect(x, y, bird.w, bird.h);
      // top rim
      ctx.fillStyle = '#6e5a18';
      ctx.fillRect(x, y, bird.w, 2);
      ctx.fillRect(x, y + bird.h - 2, bird.w, 2);
      // dark label band
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(x, y + bird.h / 2 - 4, bird.w, 8);
      // little H monogram on the label
      ctx.fillStyle = '#e8c95a';
      ctx.fillRect(x + 6, y + bird.h / 2 - 2, 2, 4);
      ctx.fillRect(x + bird.w - 8, y + bird.h / 2 - 2, 2, 4);
      ctx.fillRect(x + 6, y + bird.h / 2, bird.w - 12, 1);

      ctx.restore();
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      // ground strip
      ctx.fillStyle = '#2a4530';
      ctx.fillRect(0, H - 4, W, 4);
      // obstacles
      for (var i = 0; i < obstacles.length; i++) drawObstacle(obstacles[i]);
      // bird
      drawBird();
      // score
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.font = 'bold 26px -apple-system,sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(score), W / 2, 38);
      ctx.font = 'bold 10px -apple-system,sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText('TOP: ' + highScore, W / 2, 54);
      // overlays
      if (!started && !dead) {
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, H / 2 - 26, W, 52);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px -apple-system,sans-serif';
        ctx.fillText('Klik / tik / spatie om te starten', W / 2, H / 2 + 4);
      } else if (dead) {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, H / 2 - 40, W, 80);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px -apple-system,sans-serif';
        ctx.fillText('Hopcone! 🌿', W / 2, H / 2 - 6);
        ctx.font = '12px -apple-system,sans-serif';
        ctx.fillText('Klik om opnieuw te spelen', W / 2, H / 2 + 18);
      }
    }

    /**
     * Fixed-timestep loop. We run the physics at exactly 60Hz regardless
     * of how often the browser fires RAF. On a 120Hz screen the loop ticks
     * twice as often but only every other tick advances physics, so the
     * world moves at the same speed; on a slow tab, missed time is caught
     * up by running multiple update steps in a single frame. Result: same
     * difficulty everywhere.
     */
    function loop(now) {
      if (!running) return;
      rafId = requestAnimationFrame(loop);

      if (!lastTime) lastTime = now || 0;
      var t = now || 0;
      var delta = t - lastTime;
      lastTime = t;
      // Cap delta after a tab switch / pause so we don't simulate 30s of
      // gravity in one frame.
      if (delta > 100) delta = 100;
      accumulator += delta;

      while (accumulator >= FIXED_DT_MS) {
        update();
        accumulator -= FIXED_DT_MS;
      }
      draw();
    }
    rafId = requestAnimationFrame(loop);

    this.stop = function () {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    };
  }

  // -------------------------------------------------------------------
  // Worker cache (read-through, write-back)
  //
  // The worker is purely a shared cache — the actual scrape still happens
  // client-side via the proxy chain. A cache hit means another visitor of
  // the same Untappd username completed a scrape within the last 48h, and
  // we can instantly apply their result without doing any Untappd calls.
  // -------------------------------------------------------------------
  async function tryCacheFetch(username) {
    if (!WORKER_URL) return null;
    var url = WORKER_URL.replace(/\/+$/, '') + '/cache?username=' + encodeURIComponent(username);
    try {
      var r = await fetch(url, { credentials: 'omit' });
      if (r.status === 404) return null; // expected on cache miss
      if (!r.ok) return null;
      var data = await r.json();
      if (!data || !data.cached || !Array.isArray(data.beerIds)) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function publishToCache(username, beerIds, breweryCount) {
    if (!WORKER_URL) return;
    if (!Array.isArray(beerIds) || beerIds.length === 0) return;
    var url = WORKER_URL.replace(/\/+$/, '') + '/cache';
    // Fire-and-forget — failures here don't affect the visitor; their local
    // state is already saved.
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username,
          beerIds: beerIds,
          breweryCount: breweryCount || 0
        }),
        credentials: 'omit'
      }).catch(function () { /* swallow */ });
    } catch (e) { /* swallow */ }
  }

  // -------------------------------------------------------------------
  // HTML parsing
  // -------------------------------------------------------------------

  /**
   * Beer-IDs live as data-bid="N" attributes on .beer-item elements in
   * the distinct-list. Using the attribute (not href) avoids matches
   * from recommendation widgets elsewhere on the page.
   */
  function extractBeerIds(html) {
    if (!html) return [];
    var ids = [];
    var re = /data-bid="(\d+)"/g;
    var m;
    while ((m = re.exec(html)) !== null) ids.push(m[1]);
    return ids;
  }

  /**
   * Brewery / style / country pickers are all rendered as <select> with
   * <option value="N"> children. We extract numeric values so the
   * scraper can sweep all three filter dimensions for max coverage.
   */
  function extractPickerIds(html, pickerId) {
    if (!html) return [];
    var re = new RegExp('<select id="' + pickerId + '"[\\s\\S]*?<\\/select>');
    var block = html.match(re);
    if (!block) return [];
    var ids = [];
    var optRe = /<option value="(\d+)"/g;
    var m;
    while ((m = optRe.exec(block[0])) !== null) ids.push(m[1]);
    return ids;
  }
  function extractBreweryIds(html) { return extractPickerIds(html, 'brewery_picker'); }
  function extractStyleIds(html)   { return extractPickerIds(html, 'style_picker'); }
  function extractCountryIds(html) { return extractPickerIds(html, 'country_picker'); }

  function looksPrivate(html) {
    return /set their profile to private/i.test(html) ||
           /profile is private/i.test(html);
  }

  function looksNotFound(html) {
    return /<title>\s*Untappd\s*\|\s*404/i.test(html);
  }

  // -------------------------------------------------------------------
  // Scrape orchestration — two-phase
  //
  // Phase 1 (scrapeMain) just fetches the user's /beers page (~2s) and
  // returns the 25 most-recent beer IDs plus the picker lists.
  //
  // Phase 2 (scrapeAllFilters) hammers brewery + style + country pickers
  // in a single shared worker pool with concurrency = 30. Streams
  // partial results via onBatch.
  // -------------------------------------------------------------------
  async function scrapeMain(username) {
    var cleanName = (username || '').trim().replace(/^@/, '');
    if (!/^[A-Za-z0-9_.-]{1,40}$/.test(cleanName)) {
      return { error: 'invalid-username' };
    }

    var mainHtml = await fetchViaProxy('https://untappd.com/user/' + encodeURIComponent(cleanName) + '/beers');
    if (!mainHtml) return { error: 'fetch-failed' };
    if (looksPrivate(mainHtml)) return { error: 'private' };
    if (looksNotFound(mainHtml)) return { error: 'not-found' };

    return {
      username: cleanName,
      beerIds: extractBeerIds(mainHtml),
      breweryIds: extractBreweryIds(mainHtml),
      styleIds: extractStyleIds(mainHtml),
      countryIds: extractCountryIds(mainHtml)
    };
  }

  async function scrapeAllFilters(username, breweryIds, styleIds, countryIds, alreadyKnown, onBatch, abort) {
    // Build one big work queue.
    var calls = [];
    for (var i = 0; i < breweryIds.length; i++) calls.push({ p: 'brewery_id', v: breweryIds[i] });
    for (var j = 0; j < styleIds.length;   j++) calls.push({ p: 'style_id',   v: styleIds[j]   });
    for (var k = 0; k < countryIds.length; k++) calls.push({ p: 'country_id', v: countryIds[k] });

    var ids = new Set(alreadyKnown || []);
    var total = calls.length;
    var done = 0;
    var nextIdx = 0;
    var lastEmit = 0;

    async function worker() {
      while (true) {
        if (abort && abort.stop) return;
        var idx = nextIdx++;
        if (idx >= total) return;
        var call = calls[idx];
        var html;
        try {
          html = await fetchViaProxy(
            'https://untappd.com/user/' + encodeURIComponent(username) +
            '/beers?' + call.p + '=' + call.v
          );
        } catch (_) { html = null; }
        if (html) {
          var newIds = extractBeerIds(html);
          for (var n = 0; n < newIds.length; n++) ids.add(newIds[n]);
        }
        done++;
        // Throttle UI updates — emitting every single call is wasteful;
        // every 5 keeps the progress text fluid without burning CPU.
        if (onBatch && (done - lastEmit >= 5 || done === total)) {
          lastEmit = done;
          onBatch({ done: done, total: total, ids: Array.from(ids) });
        }
      }
    }

    var workers = [];
    var poolSize = Math.min(BREWERY_CONCURRENCY, total);
    for (var w = 0; w < poolSize; w++) workers.push(worker());
    await Promise.all(workers);

    return { ids: Array.from(ids), aborted: !!(abort && abort.stop) };
  }

  // -------------------------------------------------------------------
  // DOM tagging
  //
  // Tasted cards get data-untappd-tasted="true" + a badge appended.
  // CSS handles the visual treatment (grayscale + fade on the card
  // content, full color on the badge).
  // -------------------------------------------------------------------
  /**
   * Resolve a card's Untappd beer ID from either the explicit
   * data-untappd-beer-id attribute (collection cards) or, as a fallback,
   * the trailing numeric segment of data-untappd-url (product-detail
   * card, where only the URL is present).
   */
  function getBeerId(card) {
    var explicit = (card.getAttribute('data-untappd-beer-id') || '').trim();
    if (explicit) return explicit;
    var url = card.getAttribute('data-untappd-url') || '';
    var m = url.match(/\/b\/[^\/?#]+\/(\d+)/);
    return m ? m[1] : '';
  }

  function applyToCard(card) {
    var id = getBeerId(card);
    var tasted = id !== '' && beerIdSet.has(id);

    // Clean up any previous state — supports re-scrape and Clear.
    var existingBadge = card.querySelector('.' + BADGE_CLASS);
    if (existingBadge) existingBadge.remove();
    card.removeAttribute('data-untappd-tasted');

    if (!tasted) return;

    card.setAttribute('data-untappd-tasted', 'true');

    var badge = document.createElement('span');
    badge.className = BADGE_CLASS;
    badge.setAttribute('aria-label', 'Al geproefd op Untappd');
    badge.textContent = 'Al geproefd';

    // Preferred placement: inside the gold overlay rating chip
    // (.review-wrapper--untappd-badge), so the badge sits within the same
    // visual unit as the Untappd rating itself.
    //
    // Fall-throughs for cards that don't render the rating as an overlay:
    //   - after the inline rating row (collection cards in some themes)
    //   - inside the details container (no rating at all)
    //   - directly on the card (last resort)
    var overlay = card.querySelector('.review-wrapper--untappd-badge');
    var inlineRating = card.querySelector('.review-wrapper--untappd:not(.review-wrapper--untappd-badge)');
    var details = card.querySelector('.product-card__details');
    if (overlay) {
      overlay.appendChild(badge);
    } else if (inlineRating && inlineRating.parentNode) {
      inlineRating.parentNode.insertBefore(badge, inlineRating.nextSibling);
    } else if (details) {
      details.appendChild(badge);
    } else {
      card.appendChild(badge);
    }
  }

  function applyToAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(CARD_SELECTOR).forEach(applyToCard);
  }

  // -------------------------------------------------------------------
  // UI rendering
  // -------------------------------------------------------------------
  function renderWidget(widget) {
    var input = widget.querySelector('[data-untappd-user-filter-input]');
    var clear = widget.querySelector('[data-untappd-user-filter-clear]');
    var submitLabel = widget.querySelector('[data-untappd-user-filter-submit-label]');

    if (input && state.username && input.value === '') input.value = state.username;

    var hasData = beerIdSet.size > 0;
    if (clear) clear.hidden = !hasData && !state.username;
    if (submitLabel) submitLabel.textContent = hasData ? 'Opnieuw ophalen' : 'Ophalen';

    renderStatus(widget);
  }

  function renderStatus(widget, override) {
    var status = widget.querySelector('[data-untappd-user-filter-status]');
    if (!status) return;
    if (override) {
      status.textContent = override.text || '';
      status.dataset.error = override.error ? 'true' : 'false';
      return;
    }
    if (beerIdSet.size === 0) {
      status.textContent = '';
      status.dataset.error = 'false';
      return;
    }
    var line = beerIdSet.size + ' bieren geladen voor ' + state.username;
    if (state.purchasedCount && state.purchasedCount > 0) {
      // Append "waarvan X bij De Hoppenaar gekocht" — nicer to lead with the
      // big number, then qualify it.
      line += ', waarvan ' + state.purchasedCount + ' bij De Hoppenaar gekocht';
    }
    line += '.';
    status.textContent = line;
    status.dataset.error = 'false';
  }

  function renderAllWidgets() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(renderWidget);
  }

  function setStatus(widget, text, isError) {
    renderStatus(widget, { text: text, error: !!isError });
  }

  // -------------------------------------------------------------------
  // Event wiring
  // -------------------------------------------------------------------
  function wireWidget(widget) {
    if (widget.dataset.untappdWired === 'true') return;
    widget.dataset.untappdWired = 'true';

    var form = widget.querySelector('[data-untappd-user-filter-form]');
    if (form) {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();
        var input = widget.querySelector('[data-untappd-user-filter-input]');
        var submit = widget.querySelector('[data-untappd-user-filter-submit]');
        var username = input ? input.value.trim() : '';
        if (!username) {
          setStatus(widget, 'Vul je Untappd-gebruikersnaam in.', true);
          return;
        }
        if (submit) submit.disabled = true;
        setStatus(widget, 'Bezig met laden…', false);

        try {
          // ---- Phase 0: Worker cache lookup (instant if hit) --------
          var cached = await tryCacheFetch(username);
          if (cached && cached.beerIds && cached.beerIds.length > 0) {
            state.username = cached.username || username;
            state.beerIds = cached.beerIds.map(String);
            state.breweryCount = cached.breweryCount || 0;
            state.purchasedCount = cached.purchasedCount || 0;
            state.fetchedAt = Date.now();
            beerIdSet = new Set(state.beerIds);
            saveState();
            renderAllWidgets();
            applyToAll();
            if (submit) submit.disabled = false;
            return;
          }

          // ---- Phase 1: main page (fast, ~2s) -----------------------
          var main = await scrapeMain(username);
          if (main.error) {
            var msg = 'Kon je profiel niet ophalen.';
            if (main.error === 'invalid-username') msg = 'Ongeldige gebruikersnaam.';
            if (main.error === 'not-found') msg = 'Gebruiker niet gevonden op Untappd.';
            if (main.error === 'private') msg = 'Dit Untappd-profiel staat op privé.';
            if (main.error === 'fetch-failed') msg = 'Geen verbinding met Untappd. Probeer over een paar minuten opnieuw.';
            setStatus(widget, msg, true);
            if (submit) submit.disabled = false;
            return;
          }

          // Commit phase-1 results immediately — badges show up now.
          state.username = main.username;
          state.beerIds = main.beerIds.map(String);
          state.breweryCount = main.breweryIds.length;
          // Reset on fresh scrape — purchasedCount comes from the Worker
          // cache (KV) after we re-fetch it post-publish below.
          state.purchasedCount = 0;
          state.fetchedAt = Date.now();
          beerIdSet = new Set(state.beerIds);
          saveState();
          renderAllWidgets();
          applyToAll();

          if (main.breweryIds.length === 0) {
            setStatus(widget, state.beerIds.length + ' bieren geladen voor ' + state.username + '.', false);
            if (submit) submit.disabled = false;
            return;
          }

          // Re-enable submit — visitor can browse while phase 2 runs.
          if (submit) submit.disabled = false;
          setStatus(widget,
            '✓ ' + state.beerIds.length + ' recente bieren — overige check-ins worden op de achtergrond aangevuld…',
            false);

          // Spin up Flappy Hoppenaar so visitors have something to do while
          // we scrape. Stop + remove any prior game first (if user spammed
          // Ophalen we don't want two canvases stacked).
          if (widget._flappyGame) {
            try { widget._flappyGame.stop(); } catch (_) {}
            widget._flappyGame = null;
          }
          try {
            widget._flappyGame = new HoppenaarFlappy(widget.querySelector('.untappd-user-filter__inner'));
          } catch (_) { /* if anything blows up here, scrape still proceeds */ }

          // ---- Phase 2: brewery + style + country sweep ------------
          // Fire-and-forget so the UI stays interactive.
          scrapeAllFilters(main.username, main.breweryIds, main.styleIds, main.countryIds, state.beerIds, function (batch) {
            state.beerIds = batch.ids;
            beerIdSet = new Set(state.beerIds);
            saveState();
            applyToAll();
            var pct = Math.round(batch.done / batch.total * 100);
            setStatus(widget,
              state.beerIds.length + ' bieren — ' + pct + '% (' +
              batch.done + ' van ' + batch.total + ' filters)',
              false);
          }).then(function () {
            state.fetchedAt = Date.now();
            saveState();
            renderAllWidgets();
            // Publish to the Worker cache so the next visitor of the same
            // username gets an instant load. Then immediately re-fetch the
            // cache to pull in the purchased-at-Hoppenaar union data, so
            // the visitor sees "X bieren, waarvan Y bij ons gekocht" in
            // the status right after their first scrape.
            publishToCache(state.username, state.beerIds, state.breweryCount);
            tryCacheFetch(state.username).then(function (refreshed) {
              if (!refreshed || !Array.isArray(refreshed.beerIds)) return;
              state.beerIds = refreshed.beerIds.map(String);
              state.purchasedCount = refreshed.purchasedCount || 0;
              beerIdSet = new Set(state.beerIds);
              saveState();
              applyToAll();
              renderAllWidgets();
            });
          }).catch(function (err) {
            setStatus(widget,
              state.beerIds.length + ' bieren geladen (achtergrond afgebroken: ' +
              (err && err.message || 'onbekende fout') + ')',
              false);
          }).then(function () {
            // Stop Flappy when scrape is fully done (success OR error).
            if (widget._flappyGame) {
              try { widget._flappyGame.stop(); } catch (_) {}
              widget._flappyGame = null;
            }
          });
        } catch (err) {
          if (submit) submit.disabled = false;
          setStatus(widget, 'Onverwachte fout: ' + (err && err.message), true);
        }
      });
    }

    var clear = widget.querySelector('[data-untappd-user-filter-clear]');
    if (clear) {
      clear.addEventListener('click', function () {
        state = defaultState();
        beerIdSet = new Set();
        saveState();
        var input = widget.querySelector('[data-untappd-user-filter-input]');
        if (input) input.value = '';
        // Kill the game if it's still up from a previous scrape.
        if (widget._flappyGame) {
          try { widget._flappyGame.stop(); } catch (_) {}
          widget._flappyGame = null;
        }
        renderAllWidgets();
        applyToAll();
      });
    }

    renderWidget(widget);
  }

  // -------------------------------------------------------------------
  // Mutation observer — re-apply to AJAX-injected cards / widgets
  // -------------------------------------------------------------------
  function startObserver() {
    if (!document.body || typeof MutationObserver === 'undefined') return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(CARD_SELECTOR)) applyToCard(node);
          if (node.matches && node.matches(WIDGET_SELECTOR)) wireWidget(node);
          if (node.querySelectorAll) {
            node.querySelectorAll(CARD_SELECTOR).forEach(applyToCard);
            node.querySelectorAll(WIDGET_SELECTOR).forEach(wireWidget);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // -------------------------------------------------------------------
  // Init
  // -------------------------------------------------------------------
  function init() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(wireWidget);
    applyToAll();
    startObserver();
  }

  window.__untappdUserFilter = {
    applyToAll: applyToAll,
    applyToCard: applyToCard,
    scrapeMain: scrapeMain,
    scrapeAllFilters: scrapeAllFilters,
    getState: function () { return Object.assign({}, state); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
